// Compact "time-since" helper for the sidebar and chat header.
// Renders ≤3 chars: "now" under one minute, then "Nm" / "Nh" / "Nd". Sub-minute
// resolution (e.g. "30s") was removed in Sprint 13 — sidebar timestamps now
// fire only at turn boundaries and seconds carry no actionable signal.
// Edge case E8: the input is a server-generated ISO string, so even if the
// client clock is skewed the relative result is sane to within a few seconds.

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';

  const diff = Math.max(0, now - t);

  if (diff < MINUTE) return 'now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h`;
  return `${Math.floor(diff / DAY)}d`;
}

// Sprint runner-checkout-freshness (ADR D-1073, VISIBILITY half) —
// D-1035: staleness is derived at read time from a timestamp, NEVER
// stored as a boolean. `windowMs` defaults to 180s (6 missed 30s
// heartbeats) — deliberately a DIFFERENT window from O7's ~24h control-
// plane check (per the Design Challenger note: the two staleness
// windows are intentionally distinct and must not be unified into one
// shared helper). A null/missing `lastSeen` (never reported) is
// treated as stale — there is nothing fresh to show.
const DEFAULT_STALE_WINDOW_MS = 180_000;

export function isStale(
  lastSeen: string | null | undefined,
  windowMs: number = DEFAULT_STALE_WINDOW_MS,
  now: number = Date.now(),
): boolean {
  if (!lastSeen) return true;
  const t = Date.parse(lastSeen);
  if (!Number.isFinite(t)) return true;
  return now - t > windowMs;
}
